import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const keyBenefits = [
  { icon: "fas fa-chart-line", title: "Strong capital appreciation and rental returns" },
  { icon: "fas fa-file-invoice-dollar", title: "Tax-free property ownership" },
  { icon: "fas fa-trowel-bricks", title: "High-quality construction and modern designs" },
  { icon: "fas fa-people-roof", title: "Safe, family-friendly communities" },
  { icon: "fas fa-arrow-trend-up", title: "Growing demand for residential properties" },
  { icon: "fas fa-shield-halved", title: "Securing a future-ready asset" },
];

const houseOptions = [
  { icon: "fas fa-house", title: "Villas", text: "Spacious homes ideal for families, offering privacy, gardens, and premium amenities." },
  { icon: "fas fa-city", title: "Townhouses", text: "A perfect blend of affordability and luxury, with community living and modern layouts." },
  { icon: "fas fa-water", title: "Waterfront Homes", text: "Exclusive properties with scenic views, located in prime areas like Yas Island and Saadiyat Island." },
  { icon: "fas fa-file-signature", title: "Off-Plan Houses", text: "Flexible payment options with strong future appreciation potential." },
];

const faqs = [
  {
    icon: "fas fa-house",
    question: "Is it a good decision to buy a house in Abu Dhabi?",
    answer: "Yes. You have decided it is right to buy a house in Abu Dhabi because Abu Dhabi offers a great lifestyle, the best business and career opportunities, and great return potential.",
  },
  {
    icon: "fas fa-globe",
    question: "Can foreigners buy houses in Abu Dhabi?",
    answer: "Yes. Foreigners can buy houses in Abu Dhabi. However, they can buy only in designated areas.",
  },
  {
    icon: "fas fa-city",
    question: "What types of houses are available in Abu Dhabi?",
    answer: "You find all different types of house options in Abu Dhabi, such as villas, apartments, townhouses, and waterfront homes.",
  },
  {
    icon: "fas fa-handshake",
    question: "Why should I choose NV Real Estate?",
    answer: "NV Real Estate has built a good business relationship with top and trusted developers in Abu Dhabi. So, we can help you buy the best property for your needs.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Buy And Sale House in Abu Dhabi", item: "https://nvrealestate.ae/buy-house-abu-dhabi" },
  ],
};

function BuyHouseAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Abu Dhabi Homes For Sale | Buy House in Abu Dhabi</title>
        <meta
          name="description"
          content="Searching for the best Abu Dhabi homes for sale? NV Real Estate has access to the best developers and their house for sale in Abu Dhabi. Connect with us not to buy house in Abu Dhabi."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/buy-house-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi home buying experts</span>
              <h1 id="villa-page-title">Buy a House in Abu Dhabi with Trusted Real Estate Experts</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore properties
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/buy-house-abu-dhabi/02.png"
                alt="Houses for sale in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Trusted experts</strong>
                <span>Helping buyers make well-informed decisions</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Buy a House in Abu Dhabi with Trusted Real Estate Experts</h2>
              <p>
                Nova Vision Real Estate is the most dependable name in Abu Dhabi's real estate
                world. We have created our specific place in the market by providing exact match
                solutions to our clients. When someone comes to us to buy a house for them, we
                listen to them very carefully to understand exactly what they are looking for, what
                their preferences and goals are, and what their budget is. Having determined their
                purpose and budget, we come up with the best options for them. We help them explore
                all suggested options and enable them to make well-informed decisions.
              </p>
              <p>
                Many people have already purchased their dream homes with our help, and almost all
                of them appreciate our approach, professionalism, and commitment. Connect with us
                now to discuss your requirements, and you will realize on your own why people
                appreciate us.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-buy-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why buy here</span>
              <h2 id="why-buy-heading">Why Buy a House in Abu Dhabi?</h2>
              <p>
                Abu Dhabi is the best place to buy your home. For sure, you will ask for the
                reasons. This will benefit you in many ways.
              </p>
              <p><strong>Key Benefits:</strong></p>
            </div>
            <div className="villa-benefits__grid">
              {keyBenefits.map((benefit, index) => (
                <article className="villa-benefit-card" key={benefit.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={benefit.icon} /></span>
                  <h3>{benefit.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="options-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">House options</span>
              <h2 id="options-heading">House Options in Abu Dhabi</h2>
              <p>NV Real Estate brings all the different home options for different needs and budgets.</p>
            </div>
            <div className="villa-benefits__grid">
              {houseOptions.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              No matter your goal, we help you find the right house sale in Abu Dhabi opportunity
              with clarity and confidence.
            </p>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Trust Nova Vision Real Estate to Buy a House in Abu Dhabi?</h2>
                <p>
                  Selecting the right real estate partner is crucial when you plan to buy a house in
                  Abu Dhabi.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  Explore the best Abu Dhabi homes for sale today and make a confident move toward
                  your ideal home or investment.
                </p>
              </div>
              <ul>
                <li><strong>Market Expertise</strong> &mdash; We provide deep insights into market trends, pricing, and investment opportunities.</li>
                <li><strong>Exclusive Access</strong> &mdash; We connect you with off-market and premium listings not available publicly.</li>
                <li><strong>Transparency</strong> &mdash; Every property is verified, with clear and honest information.</li>
                <li><strong>End-to-End Support</strong> &mdash; From property selection to paperwork and final purchase, we manage the entire process.</li>
                <li><strong>Client-Focused Approach</strong> &mdash; We align every recommendation with your budget, lifestyle, and long-term goals.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Buyer questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your home search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`buy-house-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`buy-house-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Homes</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Purchase Support</strong><span>Offer to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email your
              requirements to us at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}

export default BuyHouseAbuDhabi;